import React, { useState, useEffect } from 'react';
import { useQuery } from 'react-query';
import { get_all_notification } from '../drawer-messenger/api/notificationApi'; // Ensure correct import path
import { formatDateWithTime } from '../../../../app/helper-functions/DateFunction';

// Define the type for a notification item
type NotificationItem = {
    id: number;
    notification_title: string;
    notification_type: string;
    notification_description: { description: string } | null;
    data: { leave_policy?: string; dates?: string } | null;
    created_at: string;
    updated_at: string;
};

function ViewAllNotifications() {
    // State for pagination and results per page
    const [currentPage, setCurrentPage] = useState(1);
    const [selectedItemsPerPage, setSelectedItemsPerPage] = useState(10); // Renamed for consistency
    const [totalItems, setTotalItems] = useState(0); // State to store total items from API

    // State to hold the list of notifications for the current page
    const [notificationList, setNotificationList] = useState<NotificationItem[]>([]);

    // Options for items per page dropdown
    const itemsPerPageOptions = [10,20,30,40, 50]; // Example options

    // --- Data Fetching with react-query ---
    const {
        data: paginationData, // This will hold the API response: { data: NotificationItem[], total_items: number, ... }
        isLoading,
        isError,
        // refetch: refetchNotifications // Keep refetch if mutations are added later
    } = useQuery(
        // Query key includes all dependencies affecting the API call
        ['get_all_notification', currentPage, selectedItemsPerPage],
        // Query function calls the API with current state values
        () => get_all_notification(currentPage, selectedItemsPerPage),
        {
            refetchOnWindowFocus: false,
            keepPreviousData: true, // Keep previous data visible while fetching the next page
            onSuccess: (data) => {
                // Assuming API returns { data: NotificationItem[], total_items: number, ... }
                // Add a check for data.data to ensure it's an array
                if (data && Array.isArray(data.data.notifications)) {
                    setNotificationList(data.data.notifications); // Set the list to data for the current page
                    setTotalItems(data.total_items || 0); // Ensure total_items is a number, default to 0
                } else {
                    console.error("API response data format is unexpected:", data);
                    setNotificationList([]);
                    setTotalItems(0);
                }
            },
            onError: (error: any) => {
                 console.error("Error fetching notifications:", error);
                 // Optionally show a toast message here
                 // toast.error("Failed to load notifications.");
                 setNotificationList([]); // Clear list on error
                 setTotalItems(0); // Reset total items on error
            },
        }
    );
    // --- End Data Fetching ---

    // Calculate total pages based on totalItems and items per page
    const totalPages = Math.ceil(totalItems / selectedItemsPerPage);

    // Reset page to 1 when items per page changes (or filters/search were added)
    useEffect(() => {
        // If selectedItemsPerPage changes and we are not already on page 1, reset to 1.
        // The react-query key change handles the refetch automatically.
        if (currentPage !== 1) {
            setCurrentPage(1);
        }
        // Add other dependencies here if you add search, filter, sort states
    }, [selectedItemsPerPage]); // Dependency array

    // --- Handlers for Pagination and Items Per Page ---

    const handleItemsPerPageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        setSelectedItemsPerPage(Number(e.target.value));
        // currentPage will be reset to 1 by the useEffect above if necessary
    };

    const handlePreviousPage = () => {
        if (currentPage > 1) {
            setCurrentPage(currentPage - 1);
        }
    };

    const handleNextPage = () => {
        // Only go to next page if not on the last page
        if (currentPage < totalPages) {
            setCurrentPage(currentPage + 1);
        }
    };
    // --- End Handlers ---

    // Helper to get badge class (Keep as is)
    const getBadgeClass = (type: string) => {
        switch (type?.toLowerCase()) { // Add ?. for safety
            case 'approved':
                return 'badge badge-light-success'; // Using Metronic's light badges
            case 'rejected':
                return 'badge badge-light-danger';
            case 'leave_approval':
                return 'badge badge-light-warning';
            default:
                return 'badge badge-light-secondary';
        }
    };

    // Calculate the range of items being shown on the current page
    const firstItemIndex = totalItems > 0 ? (currentPage - 1) * selectedItemsPerPage + 1 : 0;
    const lastItemIndex = totalItems > 0 ? Math.min(currentPage * selectedItemsPerPage, totalItems) : 0;


    return (
        <div className="card shadow-sm mb-5 mb-xl-10">
            <div className="card-header bg-light border-0">
                <h3 className="card-title fw-bold text-primary m-0">
                     All Notifications
                     {/* Display total items here if you like */}
                     {/* {totalItems > 0 && ` (${totalItems} Total)`} */}
                     {isLoading && !notificationList.length && ` (...)`} {/* Show loading indicator next to title on initial load */}
                </h3>
            </div>

            <div className="card-body py-3">
                <div className="table-responsive">
                    <table className="table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4">
                        <thead>
                            <tr className="fw-bold text-muted">
                                <th className="min-w-200px">Title</th>
                                <th className="min-w-100px">Type</th>
                                <th className="min-w-300px">Description</th>
                                <th className="min-w-150px">Details</th> 
                                <th className="min-w-150px">Created At</th>
                            </tr>
                        </thead>
                        <tbody>
                            {isLoading && notificationList.length === 0 ? (
                                <tr>
                                    <td colSpan={5} className='text-center'>
                                        <div className='spinner-border' role='status'>
                                            <span className='visually-hidden'>Loading...</span>
                                        </div>
                                        <p className='mt-2'>Loading notifications...</p>
                                    </td>
                                </tr>
                            ) : isError ? (
                                <tr>
                                    <td colSpan={5} className='text-center text-danger'>Error loading notifications. Please try again.</td>
                                </tr>
                            ) : notificationList && notificationList.length > 0 ? (
                                notificationList.map((notification) => (
                                    <tr key={notification.id}>
                                        <td>
                                            <div className="text-dark fw-bold fs-6">
                                                {notification.notification_title?.replace(/([a-z])([A-Z])/g, '$1 $2')}
                                            </div>
                                        </td>
                                        <td>
                                            <span className={getBadgeClass(notification.notification_type)}>
                                                {notification.notification_type?.replace('_', ' ') || 'N/A'}
                                            </span>
                                        </td>
                                        <td>
                                            <p className="text-dark mb-0">
                                                {notification.notification_description?.description || 'No description.'}
                                            </p>
                                        </td>
                                        <td>
                                             {notification.data?.leave_policy && (
                                                 <div className="text-dark"><strong>Leave Policy:</strong> {notification.data.leave_policy}</div>
                                             )}
                                             {notification.data?.dates && (
                                                 <div className="text-dark"><strong>Dates:</strong> {notification.data.dates}</div>
                                             )}
                                             {!notification.data?.leave_policy && !notification.data?.dates && 'N/A'}
                                        </td>
                                        <td>
                                            <div className="text-dark">
                                                {formatDateWithTime(notification.created_at)}
                                            </div>
                                        </td>
                                    </tr>
                                ))
                            ) : (
                                !isLoading && !isError && notificationList.length === 0 && (
                                    <tr>
                                        <td colSpan={5} className='text-center text-muted'>No notifications found matching the criteria.</td>
                                    </tr>
                                )
                            )}
                             {/* Loading state while fetching *next* page data (keepPreviousData shows old data) */}
                             {isLoading && notificationList.length > 0 && (
                                  <tr>
                                     {/* Colspan should match the number of columns (5 in this case) */}
                                     <td colSpan={5} className='text-center text-muted'>
                                         <div className='spinner-border spinner-border-sm' role='status'>
                                             <span className='visually-hidden'>Loading...</span>
                                         </div> Loading next page...
                                     </td>
                                 </tr>
                             )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* Pagination Footer - ALWAYS SHOW */}
            {/* Removed the conditional rendering {(totalItems > 0 || isLoading) && (...)} */}
            <div className='card-footer d-flex justify-content-between py-4 align-items-center'>
                <div className='d-flex align-items-center'>
                    <span className='me-2 text-muted'>Results Per Page:</span>
                    <select
                        className='form-select form-select-sm form-select-solid'
                        value={selectedItemsPerPage}
                        onChange={handleItemsPerPageChange}
                        disabled={isLoading} // Disable select while loading
                    >
                        {itemsPerPageOptions.map(option => (
                            <option key={option} value={option}>{option}</option>
                        ))}
                    </select>
                     {/* Show item range info only if totalItems > 0 or while loading */}
                     {(totalItems > 0 || isLoading) && (
                          <span className='ms-3 text-muted'>
                              {isLoading && !notificationList.length ? 'Loading...' : `${firstItemIndex} to ${lastItemIndex} of ${totalItems}`}
                          </span>
                     )}
                     {/* Handle case where totalItems is 0 after loading */}
                     {!isLoading && totalItems === 0 && (
                          <span className='ms-3 text-muted'>
                             Showing 0 to 0 of 0
                          </span>
                     )}
                </div>
                <div className='d-flex justify-content-end'>
                     {/* Show current page number */}
                     <span className='me-2 py-2 text-muted'>Page {currentPage} of {totalPages || 1}</span> {/* Show total pages, default to 1 if 0 items */}

                    <button
                        className='btn btn-light me-2'
                        onClick={handlePreviousPage}
                        disabled={currentPage === 1 || isLoading || totalItems === 0} // Disable if on first page, loading, or no items
                    >
                        Previous
                    </button>
                    <button
                        className='btn btn-light'
                        onClick={handleNextPage}
                        disabled={currentPage >= totalPages || isLoading || totalItems === 0} // Disable if on last page, loading, or no items
                    >
                        Next
                    </button>
                </div>
            </div>
             {/* End Pagination Footer */}

        </div>
    );
}

export default ViewAllNotifications;